import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { ColoursObj, Icon, TEventrIcons, Typography } from "@eventr/common-storybook";
import { Box } from "@mui/material";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
const opacityFrames = `
    @keyframes highlight {
        0% {
            opacity: 1;
        }

        50%{
            opacity: 0.5;
        }
        
        100% {
            opacity: 1;
        }
    }
`;

interface INavBarItemProps {
  id: string;
  label: string;
  icon: TEventrIcons;
  path: string;
  active?: boolean;
  /** The item cannot be navigated to. */
  disabled?: boolean;
  /** Draws the disabled item in the muted style. */
  dimWhenDisabled?: boolean;
  /** Runs on press while disabled, in place of navigating. */
  onDisabledPress?: () => void;
  pulse?: boolean;
  count?: number;
  /** Runs on press, including when the item's path is already open. */
  onClick?: () => void;
}

export const NavBarItem = ({
  id,
  label,
  icon,
  path,
  active,
  disabled,
  dimWhenDisabled,
  onDisabledPress,
  pulse,
  count = 0,
  onClick,
}: INavBarItemProps) => {
  const { pushPath } = useGlobalRouter();
  const dimmed = disabled && dimWhenDisabled;
  // No count while disabled.
  const badge = disabled ? 0 : count;
  return (
    <Box
      id={id}
      onClick={() => {
        if (disabled) {
          if (!onDisabledPress) return;
          Haptics.impact({ style: ImpactStyle.Light });
          onDisabledPress();
          return;
        }
        onClick?.();
        if (path === window.location.pathname) return;

        Haptics.impact({ style: ImpactStyle.Light });
        pushPath(path, { animation: "none" });
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "80px",
        height: "46px",
        animation: pulse ? "highlight 2s infinite ease" : undefined,
      }}
    >
      {!!badge && (
        <Box
          sx={{
            position: "absolute",
            display: "grid",
            backgroundColor: ColoursObj.states.error,
            borderRadius: "50%",
            height: "16px",
            width: "16px",
            justifyItems: "center",
            alignItems: "center",
            translate: "16px -22px",
          }}
        >
          <Typography
            color={ColoursObj.shades.shade5}
            variant="bodySubtext"
            sx={{
              fontSize: badge > 9 ? "8px" : "12px",
              lineHeight: badge > 9 ? "8px" : "12px",
            }}
          >
            {badge}
          </Typography>
        </Box>
      )}
      <Icon
        iconColour={
          active ? ColoursObj.navbar.highlighted : dimmed ? ColoursObj.shades.shade1 + "30" : ColoursObj.navbar.icons
        }
        name={icon}
        width="28px"
        height="28px"
      />
      <Typography
        color={
          active ? ColoursObj.navbar.highlighted : dimmed ? ColoursObj.shades.shade1 + "30" : ColoursObj.navbar.icons
        }
        variant="bodySubtext"
      >
        {label}
      </Typography>
      <style>{opacityFrames}</style>
    </Box>
  );
};
